/**
 * S5 · Reveal and share (design files 4 / 4w).
 *
 * Retake sequence (upload-first, delete-last — profile never points at a
 * deleted path):
 *   1. getUploadUrl() → B
 *   2. PUT selfie to B                       (A and B both exist — safe window)
 *   3+4. attach_selfie_to_user(B) — RPC binds consent AND swaps
 *        consumer_profiles.selfie_storage_path = B atomically (SECURITY DEFINER)
 *   5. supabase.storage.from('selfies').remove([A]) — A now 404s
 * If step 5 fails, retake fails; A is caught by the age-gated reaper predicate
 * (objects in selfies/{user}/ whose path <> current selfie_storage_path).
 * A's consent_records row is retained (append-only ledger) and is inert
 * because the underlying storage object no longer exists.
 *
 * LEGAL GATES (L1): no card scheme mark/name. The design's upsell bundle and
 * sticky-bar price were fabricated marketing figures, not real values from this
 * order — omitted entirely per the money-amount gate. No price is typed here or
 * in this comment: any figure shown to a customer resolves from programme config
 * (see src/lib/programme-figures.ts).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  attachSelfieToUser,
  countGenerationAttempts,
  cutoutOrOriginal,
  getUploadUrl,
  isStaffGenerationExempt,
  listGenerationsForPick,
  startGeneration,
  pollGenerations,
  uploadSelfie,
} from "@/services/avatar";
import { resolveFreeAttempts } from "@/lib/consumer-config";
import { describeGenerationError } from "@/lib/generation-errors";

import {
  CardCustomization,
  readCardCustomization,
  readSelfiePath,
  readStylePick,
  saveCardCustomization,
  saveSelectedVariant,
  saveSelfiePath,
  recordFunnelEvent,
} from "@/lib/consumer-session";
import { findTile, tileColor } from "@/lib/style-art";
import { resolveTaxonomyPick } from "@/lib/taxonomy-link";
import { useConsumerFlow } from "../ConsumerFlow";
import {
  CardFace,
  Display,
  FunnelContainer,
  FunnelHeader,
  FunnelPage,
  GhostButton,
  SentenceLink,
  Pill,
  PrimaryButton,
  SectionTitle,
  StickyBar,
  inputClass,
} from "@/components/consumer/chrome";
import { cn } from "@/lib/utils";
import { useSiteContent } from "@/hooks/useSiteContent";
import { resolveConsumerColours } from "@/lib/consumer-colours";
import { readSessionGenerationIds, rememberSessionGenerations } from "@/lib/generation-session";
import { saveDesignedCard } from "@/lib/designed-card";
import { DesignedCardFace } from "@/components/consumer/DesignedCardFace";
import { S5_SECTION, S5_DEFAULTS } from "@/content/screens/s5";

type Variant = {
  id: string;
  variant_number: number;
  generated_image_url: string | null;
  status: string;
  created_at?: string | null;
};

/** Chronological ordering of the accumulated set (oldest generation first). */
function sortVariants(rows: Variant[]): Variant[] {
  return [...rows].sort((a, b) => {
    const ta = a.created_at ? Date.parse(a.created_at) : 0;
    const tb = b.created_at ? Date.parse(b.created_at) : 0;
    if (ta !== tb) return ta - tb;
    return a.variant_number - b.variant_number;
  });
}


/**
 * There is NO code minted here. Browser-minted referral codes were removed:
 * they resolved against nothing and were broadcast alongside a discount that
 * program config does not grant. Real codes come from the B7 issuer only.
 */



/** A URL already pointing at the persisted cutout needs no second pass. */
const isCutoutUrl = (url?: string | null) => !!url && url.includes("/cutouts/");

export default function S5Reveal() {
  const { t } = useSiteContent(S5_SECTION, S5_DEFAULTS);
  const { program } = useConsumerFlow();
  const navigate = useNavigate();
  const location = useLocation();
  const stateIds: string[] = (location.state as any)?.generationIds ?? [];
  const [variants, setVariants] = useState<Variant[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const revealLogged = useRef(false);
  const [retaking, setRetaking] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // §1 analytics: the reveal is the funnel's pivot point — record it once, the
  // moment a variant is actually on screen.
  useEffect(() => {
    if (revealLogged.current || variants.length === 0) return;
    revealLogged.current = true;
    recordFunnelEvent(program.slug, "revealed", program.program_id, { variants: variants.length });
  }, [variants.length, program.slug, program.program_id]);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const pick = readStylePick(program.slug);
  const art = findTile(program.styles, pick?.style_id);
  // The resolved 3-tier pick — also the scope key for the picker's generation set.
  // An unknown slug (stale draft naming a tile the programme no longer exposes)
  // resolves to null here and S4 is the screen that reports it.
  const triple = useMemo(() => {
    try { return resolveTaxonomyPick(pick, program.config, program.styles); } catch { return null; }
  }, [pick?.style_id, pick?.sub_style_id, pick?.hobby_id, program.config, program.styles]);
  const existingCustom = readCardCustomization(program.slug);
  // Programme-configurable palette (public_config.colorChoices); falls back to
  // the FULL set so the surface can never silently render a partial palette.
  const swatches = resolveConsumerColours((program.config as any)?.colorChoices);

  const [colour, setColour] = useState(existingCustom?.colour ?? tileColor(program.styles, pick?.style_id));
  const [cname, setCname] = useState(existingCustom?.cname ?? art?.name ?? "your colorway");
  const [orientation, setOrientation] = useState<"portrait" | "landscape">(existingCustom?.orientation ?? "portrait");
  const [size, setSize] = useState(existingCustom?.size ?? 100);
  // Wizard-parity framing: the avatar can be dragged, not just scaled.
  // Percentages, 50/50 = centred, matching card_requests.avatar_position.
  const [posX, setPosX] = useState(existingCustom?.pos_x ?? 50);
  const [posY, setPosY] = useState(existingCustom?.pos_y ?? 50);
  const dragRef = useRef<{ id: number; x: number; y: number; w: number; h: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  /** F10 · which single-purpose customisation step is on screen. */
  const [step, setStep] = useState<"pick" | "colour" | "orientation" | "size">("pick");
  const cutDoneRef = useRef<Set<string>>(new Set());


  // Visible allowance ("N of 5"). Display only — generate-avatar enforces it.
  const freeAttempts = resolveFreeAttempts((program.config ?? {}) as Record<string, any>);
  const [attemptsUsed, setAttemptsUsed] = useState<number | null>(null);
  // Staff (super_admin/admin) are exempt from the consumer allowance. Mirrors the
  // server-side role check in generate-avatar; server stays the authority.
  const [staffExempt, setStaffExempt] = useState(false);
  const limitReached = !staffExempt && attemptsUsed !== null && attemptsUsed >= freeAttempts;

  useEffect(() => {
    saveCardCustomization(program.slug, {
      colour,
      cname,
      orientation,
      size,
      pos_x: posX,
      pos_y: posY,
    } as CardCustomization);
  }, [program.slug, colour, cname, orientation, size, posX, posY]);

  // Keep the counter honest after every generation round.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sess } = await supabase.auth.getUser();
      if (!sess?.user) return;
      // No tenant filter: RLS already scopes this read to the caller's own rows.
      const [used, exempt] = await Promise.all([
        countGenerationAttempts(sess.user.id),
        isStaffGenerationExempt(sess.user.id),
      ]);
      if (cancelled) return;
      setAttemptsUsed(used);
      setStaffExempt(exempt);
    })();
    return () => { cancelled = true; };
  }, [variants.length, regenerating]);



  function onDragStart(e: React.PointerEvent<HTMLDivElement>) {
    const box = e.currentTarget.getBoundingClientRect();
    dragRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, w: box.width, h: box.height };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  }

  function onDragMove(e: React.PointerEvent<HTMLDivElement>) {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    // Translate pixel movement into card-relative percentages, clamped so the
    // avatar can never be dragged off the printable face.
    const dx = ((e.clientX - d.x) / d.w) * 100;
    const dy = ((e.clientY - d.y) / d.h) * 100;
    dragRef.current = { ...d, x: e.clientX, y: e.clientY };
    setPosX((p) => Math.min(85, Math.max(15, p + dx)));
    setPosY((p) => Math.min(85, Math.max(15, p + dy)));
  }

  function onDragEnd(e: React.PointerEvent<HTMLDivElement>) {
    if (dragRef.current?.id === e.pointerId) dragRef.current = null;
    setDragging(false);
  }


  useEffect(() => {
    let cancelled = false;
    const cutStarted = new Set<string>();

    // remove-background PERSISTS its result by overwriting generated_image_url
    // with the stored cutouts/<uuid>.png. So a row whose URL is already a cutout
    // must NEVER be re-cut: re-cutting an already-transparent PNG is pure waste,
    // and firing one invoke per accumulated avatar on mount (up to 10) burst the
    // function past its CPU budget — which made cutouts FAIL and fall back to
    // the raw square image. That is the "square + slow" regression.
    const isCutout = isCutoutUrl;

    // Serialised worker: at most CONCURRENCY cutouts in flight, newest first.
    const CONCURRENCY = 2;
    const queue: Variant[] = [];
    let active = 0;

    const runOne = (row: Variant) => {
      active += 1;
      cutoutOrOriginal(row.id, row.generated_image_url!)
        .then((url) => {
          cutDoneRef.current.add(row.id);
          if (!cancelled) {
            setVariants((prev) => prev.map((v) => (v.id === row.id ? { ...v, generated_image_url: url } : v)));
          }
        })
        .finally(() => {
          active -= 1;
          pump();
        });
    };

    const pump = () => {
      while (!cancelled && active < CONCURRENCY && queue.length) runOne(queue.shift()!);
    };

    const cutout = (row: Variant, priority = false) => {
      if (cutStarted.has(row.id) || !row.generated_image_url) return;
      // Already cut out server-side — nothing to do, and no invoke to waste.
      if (isCutout(row.generated_image_url)) {
        cutStarted.add(row.id);
        cutDoneRef.current.add(row.id);
        return;
      }
      cutStarted.add(row.id);
      if (priority) queue.unshift(row);
      else queue.push(row);
      pump();
    };

    const merge = (rows: Variant[], select?: string) => {
      if (!rows.length) return;
      setVariants((prev) => {
        const seen = new Map(prev.map((v) => [v.id, v]));
        rows.forEach((r) => {
          const existing = seen.get(r.id);
          // Never clobber an already cut-out URL with the raw one.
          if (!existing) seen.set(r.id, r);
          else seen.set(r.id, { ...r, generated_image_url: existing.generated_image_url ?? r.generated_image_url });
        });
        return sortVariants([...seen.values()]);
      });
      if (select) setSelectedId(select);
      else setSelectedId((cur) => cur ?? rows[rows.length - 1].id);
      // Freshly landed variants jump the queue; backfill of older raw rows waits.
      rows.forEach((r) => cutout(r, !!select));
    };

    // The picker's set: THIS style's completed generations inside the recency
    // window (24h ≈ this card attempt), not every avatar the account ever made.
    const loadHistory = async (userId: string, select?: string) => {
      if (!triple) return 0;
      const { data, error: e } = await listGenerationsForPick(userId, {
        styleId: triple.styleId,
        subStyleId: triple.subStyleId,
        themeId: triple.themeId,
      });
      if (e) throw e;
      let rows: Variant[] = ((data as any) ?? []).filter((r: Variant) => r.generated_image_url);
      // SESSION SCOPE: the strip shows only avatars from THIS card-design
      // attempt (this tab session) for THIS style. Server-side scoping can only
      // do style + recency; the session ledger narrows it to this attempt.
      const ledger = readSessionGenerationIds(program.slug, triple);
      const inSession = new Set([...ledger, ...stateIds]);
      if (inSession.size) rows = rows.filter((r) => inSession.has(r.id));
      // Fresh tab with no ledger: adopt what we found so back->forward resumes.
      rememberSessionGenerations(program.slug, triple, rows.map((r) => r.id));
      if (cancelled) return 0;
      merge(rows, select);
      return rows.length;
    };

    (async () => {
      try {
        const { data: sess } = await supabase.auth.getUser();
        if (!sess?.user) throw new Error("Not signed in — restart the flow.");

        // Show everything already generated straight away.
        const historyCount = await loadHistory(sess.user.id);

        if (stateIds.length) {
          // Progressive: poll the batch and reveal each variant as it completes.
          const landed = new Set<string>();
          // Tight poll (750ms) so a variant appears WHEN READY, not on a lazy tick.
          // Same wall-clock budget as before (~90s) at 120 × 750ms.
          for (let i = 0; i < 120 && !cancelled; i++) {
            const { data, error: e } = await supabase
              .from("avatar_generations")
              .select("id, variant_number, generated_image_url, status, created_at")
              .in("id", stateIds)
              .order("variant_number");
            if (e) throw e;
            const all: Variant[] = (data as any) ?? [];
            const fresh = all.filter((r) => r.status === "completed" && !landed.has(r.id));
            fresh.forEach((r) => landed.add(r.id));
            // The newest batch takes the selection; the rest stay pickable.
            if (fresh.length) merge(fresh, landed.size === fresh.length ? fresh[0].id : undefined);
            const settled = all.filter((r) => r.status === "completed" || r.status === "failed").length;
            if (settled >= stateIds.length) break;
            await new Promise((r) => setTimeout(r, 750));
          }

          if (cancelled) return;
          if (landed.size) return;
        }

        if (!historyCount && !cancelled) setError(t("s5.errors.noGenerations"));
      } catch (e: any) {
        if (!cancelled) setError(e?.message || String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [program.id, stateIds.join(",")]);

  const selected = useMemo(
    () => variants.find((v) => v.id === selectedId) ?? null,
    [variants, selectedId],
  );

  /**
   * THE designed card. Built here (the design screen) and persisted on every
   * change, so S6 / S7 / S8 render the exact same object through the exact same
   * component instead of re-deriving colour + framing per screen.
   */
  const designedCard = useMemo(
    () => ({
      generationId: selected?.id ?? null,
      avatarUrl: selected?.generated_image_url ?? null,
      colour,
      cname,
      orientation,
      avatarZoom: size,
      avatarPosition: { x: Math.round(posX), y: Math.round(posY) },
      variantNumber: selected?.variant_number ?? null,
    }),
    [selected, colour, cname, orientation, size, posX, posY],
  );

  useEffect(() => {
    if (!designedCard.avatarUrl) return;
    saveDesignedCard(program.slug, designedCard);
  }, [program.slug, designedCard]);

  const styleName = art?.name || pick?.style_id || "your style";

  /**
   * THE picker action. Selecting a thumbnail sets the card's avatar; the hero
   * card, the share frame and onContinue() all read `selected`, so one state
   * write drives every downstream render. If the picked row is still raw
   * (its cutout hasn't landed), kick it to the FRONT of the cutout queue so the
   * card composites transparently as soon as possible.
   */
  function selectVariant(id: string) {
    setSelectedId(id);
    setError(null);
    const row = variants.find((v) => v.id === id);
    if (row?.generated_image_url && !isCutoutUrl(row.generated_image_url) && !cutDoneRef.current.has(id)) {
      cutoutOrOriginal(id, row.generated_image_url).then((url) => {
        cutDoneRef.current.add(id);
        setVariants((prev) => prev.map((v) => (v.id === id ? { ...v, generated_image_url: url } : v)));
      });
    }
  }

  async function onRetakeFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.currentTarget.value = "";
    if (!file) return;
    setRetaking(true);
    setError(null);
    try {
      const oldPath = readSelfiePath(program.slug);
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      // Step 1: get upload URL for B
      const { data: up, error: upErr } = await getUploadUrl(ext);
      if (upErr) throw upErr;
      const signedUrl = (up as any)?.signedUrl ?? (up as any)?.data?.signedUrl;
      const newPath: string = (up as any)?.path ?? (up as any)?.data?.path;
      if (!signedUrl || !newPath) throw new Error(t("s5.errors.uploadUrlMissing"));
      // Step 2: PUT selfie to B (both A and B briefly exist — safe window)
      await uploadSelfie(signedUrl, file);
      // Step 3+4: attach_selfie_to_user binds consent to B AND atomically swaps
      // consumer_profiles.selfie_storage_path = B in one SECURITY DEFINER RPC.
      // (Server-side merge of steps 3 and 4 — invariant preserved: profile never
      // points at a deleted path.)
      const attach = await attachSelfieToUser(newPath, 1);
      if ((attach as any).error) throw (attach as any).error;
      saveSelfiePath(program.slug, newPath);
      // Step 5: SYNCHRONOUS delete of A so it 404s immediately.
      // If this fails, retake DOES NOT report success — profile stays at B (valid),
      // A becomes a reaper-eligible orphan (age-gated, path <> current path).
      if (oldPath && oldPath !== newPath) {
        const { error: delErr } = await supabase.storage
          .from("selfies")
          .remove([oldPath]);
        if (delErr) throw new Error(t("s5.errors.retakeDeleteFailed").replace("{message}", delErr.message));
      }
      navigate("../generating", { replace: true });
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setRetaking(false);
    }
  }

  async function onRegenerate() {
    if (regenerating) return;
    // Client hard-stop: don't fire a request that the server will refuse with 402.
    if (limitReached) {
      setError(`You've used all ${freeAttempts} of your free generations.`);
      return;
    }
    setRegenerating(true);
    setError(null);
    try {
      const selfiePath = readSelfiePath(program.slug);
      if (!selfiePath || !pick?.style_id) throw new Error(t("s5.errors.missingSelfieOrStyle"));
      if (!triple) throw new Error(t("s5.errors.missingSelfieOrStyle"));
      const { data, error: e } = await startGeneration({
        programId: program.program_id,
        styleId: triple.styleId,
        subStyleId: triple.subStyleId,
        themeId: triple.themeId,
        selfieStoragePath: selfiePath,
        // A regenerate is a full new BATCH, same size as S4's (program config).
        numVariants: Number((program.config as any)?.numVariants ?? 2),
        // Each regenerate is its OWN batch: without this the server replays the
        // completed idempotency entry and no new work is ever run.
        batchKey: `regen:${Date.now()}`,
      });
      if (e) throw e;
      const newIds: string[] = (data as any)?.generationIds ?? [];
      if (!newIds.length) throw new Error(t("s5.errors.regenerationFailed"));
      // The new batch belongs to this session's set.
      rememberSessionGenerations(program.slug, triple, newIds);
      // Poll briefly for completion. The poll response shape is
      // `{ generations: [{ id, status, ... }] }` — there is NO top-level
      // `status`, which is why this loop used to always time out and raise
      // "taking longer than expected" even on a fast, successful batch.
      let rows: Variant[] = [];
      for (let attempt = 0; attempt < 40; attempt++) {
        const { data: poll } = await pollGenerations(newIds);
        const gens: any[] = (poll as any)?.generations ?? [];
        const settled = gens.length > 0 && gens.every((g) => g.status === "completed" || g.status === "failed");
        const anyDone = gens.some((g) => g.status === "completed");
        if (settled || anyDone) {
          const { data: g } = await supabase
            .from("avatar_generations")
            .select("id, variant_number, generated_image_url, status, created_at")
            .in("id", newIds)
            .eq("status", "completed");
          rows = (g as any) ?? [];
          if (rows.length) break;
          if (settled) break;
        }
        await new Promise((r) => setTimeout(r, 750));
      }

      if (rows.length) {
        // Append to the accumulated set — earlier avatars stay pickable.
        setVariants((v) => sortVariants([...v.filter((x) => !rows.some((r) => r.id === x.id)), ...rows]));
        setSelectedId(rows[0].id);
        // Same downstream cutout step as the initial reveal (fallback-safe).
        rows.forEach((r) => {
          if (!r.generated_image_url) return;
          if (isCutoutUrl(r.generated_image_url)) { cutDoneRef.current.add(r.id); return; }
          cutoutOrOriginal(r.id, r.generated_image_url).then((url) => {
            cutDoneRef.current.add(r.id);
            setVariants((prev) => prev.map((v) => (v.id === r.id ? { ...v, generated_image_url: url } : v)));
          });
        });
      } else {
        throw new Error(t("s5.errors.generationSlow"));
      }
    } catch (err: any) {
      // Typed edge-function failures → consumer copy (not "non-2xx status code").
      const info = await describeGenerationError(err, freeAttempts);
      setError(info.status !== null || info.code !== "UNKNOWN" ? info.message : (err?.message || String(err)));
    } finally {
      setRegenerating(false);
    }
  }

  async function onContinue() {
    if (!selected?.generated_image_url) return;
    // Wizard-parity: never hand a RAW generation downstream. If the progressive
    // cutout for this variant hasn't landed yet, run it (fallback-to-original)
    // before staging, so S6/S7/print all composite transparently.
    let imageUrl = selected.generated_image_url;
    if (!cutDoneRef.current.has(selected.id) && !isCutoutUrl(imageUrl)) {
      imageUrl = await cutoutOrOriginal(selected.id, imageUrl);
      cutDoneRef.current.add(selected.id);
    }
    saveSelectedVariant(program.slug, {
      generation_id: selected.id,
      image_url: imageUrl,
      variant_number: selected.variant_number,
      // Wizard-parity: carry the S5 card presentation into submission.
      orientation,
      card_color: colour,
      avatar_scale: size / 100,
      // The dragged framing, not a hardcoded centre.
      avatar_position: { x: Math.round(posX), y: Math.round(posY) },

    });
    // Canonical object wins downstream — same avatar, colour, zoom, position.
    saveDesignedCard(program.slug, { ...designedCard, avatarUrl: imageUrl });
    navigate("../details");
  }


  /**
   * F10 · one screen, one decision. The reveal used to be a single long scroll
   * (pick a version, colour, orientation, size, framing, share, order). It is
   * now a stepped sequence over the SAME state and the SAME renderer — no
   * second implementation, no extra consent, nothing new collected.
   * Framing travels with size (they are the same operation).
   * Sharing lives on its own screen (`/create/share`).
   */
  const STEPS = ["pick", "colour", "orientation", "size"] as const;
  const stepIndex = STEPS.indexOf(step);

  return (
    <FunnelPage testId="s5-reveal">
      <FunnelContainer size="form">
        <FunnelHeader
          backTo={step === "pick" ? "../style" : undefined}
          onBack={step === "pick" ? undefined : () => setStep(STEPS[stepIndex - 1])}
          backLabel={t("s5.header.back")}
          step={t("s5.header.step")}
        />

        {step === "pick" ? (
          <>
            {/* C1 · no subline. "Tennis, on Tennis" restated the header and the
                tile label; the header carries the whole message. */}
            <Display className="mb-3 max-w-[16ch] md:mb-[22px]">{t("s5.header.title")}</Display>
          </>
        ) : (
          <SectionTitle className="mb-3">
            {step === "colour"
              ? t("s5.customize.background")
              : step === "orientation"
                ? t("s5.customize.orientation")
                : t("s5.customize.avatarSize")}
          </SectionTitle>
        )}

        {error && (
          <p className="mb-3 text-[14px] font-medium text-[#EC1314]" data-testid="s5-reveal-error">{error}</p>
        )}

        {/* The card is visible on every step — each adjustment shows on it.
            N13 · on the size step the slider sits VERTICALLY beside the card, so
            the control and the thing it changes are both above the fold. */}
        <div className="mb-5 flex flex-col items-center gap-3">
          <div className="flex items-stretch justify-center gap-3">
            <div
              onPointerDown={step === "size" ? onDragStart : undefined}
              onPointerMove={step === "size" ? onDragMove : undefined}
              onPointerUp={step === "size" ? onDragEnd : undefined}
              onPointerCancel={step === "size" ? onDragEnd : undefined}
              className={cn(
                "touch-none select-none",
                step === "size" ? (dragging ? "cursor-grabbing" : "cursor-grab") : "",
              )}
              role="group"
              aria-label={step === "size" ? "Drag to reposition your avatar" : undefined}
              data-testid="s5-reposition"
              data-pos-x={Math.round(posX)}
              data-pos-y={Math.round(posY)}
            >
              {/* ONE renderer, fed by THE designed-card object. */}
              <DesignedCardFace
                key={selected?.id ?? "empty"}
                card={designedCard}
                alt={styleName}
                readOnly={step !== "size"}
                data-testid="s5-hero-card"
                className={cn(
                  "shadow-[0_18px_44px_rgba(28,32,48,0.24)] animate-[cmRise_620ms_cubic-bezier(0.22,1,0.36,1)_both]",
                  orientation === "portrait" ? "w-[196px] sm:w-[268px]" : "w-[300px] sm:w-[400px] max-w-full",
                )}
              />
            </div>

            {step === "size" ? (
              <div className="flex flex-none flex-col items-center gap-2" data-testid="s5-size-control">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-muted">
                  {size}%
                </span>
                <input
                  type="range"
                  min={80}
                  max={150}
                  step={1}
                  value={size}
                  onChange={(e) => setSize(+e.target.value)}
                  aria-label="Avatar size"
                  className="w-11 flex-1 cursor-pointer accent-grape"
                  style={{ writingMode: "vertical-lr", direction: "rtl" }}
                  data-testid="s5-size"
                />
              </div>
            ) : null}
          </div>
          {step === "size" ? (
            <p className="text-center text-[12px] text-slate-muted">{t("s5.customize.dragHint")}</p>
          ) : null}
        </div>


        {step === "colour" ? (
          <div className="mb-4 flex flex-wrap justify-center gap-2" data-testid="s5-swatches">
            {swatches.map((s) => (
              <button
                key={s.colour}
                type="button"
                onClick={() => { setColour(s.colour); setCname(s.cname); }}
                aria-label={s.cname}
                className="h-11 w-11 flex-none rounded-full p-0"
                style={{
                  background: s.colour,
                  boxShadow:
                    colour === s.colour
                      ? "0 0 0 2px hsl(var(--paper)), 0 0 0 4px hsl(var(--ink))"
                      : "0 0 0 2px hsl(var(--paper)), 0 0 0 2px rgba(28,32,48,0.14)",
                }}
                data-testid={`s5-swatch-${s.cname.toLowerCase().replace(/\s+/g, "-")}`}
              />
            ))}
          </div>
        ) : null}

        {step === "orientation" ? (
          <div className="mb-4 flex justify-center">
            <div className="inline-flex flex-none gap-0.5 rounded-full bg-foreground/[0.07] p-[3px]">
              {(["portrait", "landscape"] as const).map((o) => (
                <Pill key={o} active={orientation === o} onClick={() => setOrientation(o)} data-testid={`s5-orient-${o}`}>
                  {o === "portrait" ? t("s5.customize.orientationPortrait") : t("s5.customize.orientationLandscape")}
                </Pill>
              ))}
            </div>
          </div>
        ) : null}

        {/* N13 · the size slider lives beside the card above, not here. */}



        {step === "pick" ? (
          <div className="mb-4 flex flex-col items-center gap-1.5" data-testid="s5-reveal-grid">
            <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-muted">{t("s5.versions.label")}</span>
            {staffExempt ? (
              <span className="text-[12px] font-medium text-slate-muted" data-testid="s5-attempts">
                Staff account — unmetered
              </span>
            ) : attemptsUsed !== null && (
              <span className="text-[12px] font-medium text-slate-muted" data-testid="s5-attempts">
                {t("s5.versions.attempts")
                  .replace("{used}", String(Math.min(attemptsUsed, freeAttempts)))
                  .replace("{total}", String(freeAttempts))}
              </span>
            )}

            <button
              type="button"
              onClick={onRegenerate}
              disabled={regenerating || limitReached}
              className="mt-1 mb-1 flex min-h-11 items-center gap-2 rounded-full border border-grape/40 bg-white px-4 text-[14px] font-semibold text-grape hover:border-grape disabled:cursor-not-allowed disabled:opacity-45"
              data-testid="s5-regenerate-cta"
              data-limit-reached={limitReached ? "true" : "false"}
            >
              <svg viewBox="0 0 24 24" className="h-[16px] w-[16px]">
                <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" fill="none" stroke="#8834B2" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {regenerating
                ? t("s5.actions.regenerating")
                : limitReached
                  ? t("s5.actions.regenerateLimit")
                  : t("s5.actions.regenerate")}
            </button>

            <div
              className="grid w-full max-w-[336px] grid-cols-6 justify-items-center gap-x-1.5 gap-y-3 sm:max-w-[420px]"
              data-testid="s5-variant-grid"
              data-count={variants.length}
            >
              {variants.map((v, i) => {
                const isSelected = v.id === selectedId;
                const label = i + 1;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => selectVariant(v.id)}
                    /* F18 · the selected ring sits on the TILE, not around the avatar. */
                    className={cn(
                      "flex w-full max-w-[52px] flex-col items-center gap-1.5 rounded-[10px] border-0 p-[3px]",
                      isSelected ? "bg-grape/10 ring-2 ring-grape" : "bg-transparent",
                    )}
                    data-testid={`s5-variant-${label}`}
                    data-selected={isSelected ? "true" : "false"}
                    aria-label={`Version ${label}`}
                    aria-pressed={isSelected}
                  >
                    <span
                      className="relative block aspect-[260/412] w-full overflow-hidden rounded-[7px]"
                      style={{ background: colour }}
                    >
                      {v.generated_image_url ? (
                        <img src={v.generated_image_url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-contain" />
                      ) : null}
                    </span>
                    <span className={cn("text-[11px] font-semibold", isSelected ? "text-grape" : "text-slate-muted")}>
                      {t("s5.versions.variantLabel")}{label}
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={onRegenerate}
                disabled={regenerating || limitReached}
                title={limitReached ? `You've used all ${freeAttempts} free generations` : undefined}
                className="flex w-full max-w-[46px] flex-col items-center gap-1.5 border-0 bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-45"
                data-testid="s5-regenerate"
                data-limit-reached={limitReached ? "true" : "false"}
              >
                <span className="grid aspect-[260/412] w-full place-items-center rounded-[7px] border-2 border-dashed border-foreground/[0.28]">
                  {regenerating ? (
                    <span className="text-[11px] font-semibold text-grape">{t("s5.versions.regeneratingLabel")}</span>
                  ) : (
                    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]">
                      <path d="M12 5v14M5 12h14" fill="none" stroke="#8834B2" strokeWidth="2.4" strokeLinecap="round" />
                    </svg>
                  )}
                </span>
                <span className="text-[11px] font-semibold text-grape">
                  {limitReached ? "Limit reached" : t("s5.versions.newLabel")}
                </span>
              </button>
            </div>
          </div>
        ) : null}
      </FunnelContainer>

      <StickyBar>
        {/*
          N4/N10-N14 · ONE primary CTA, pinned, full width, and it NAMES THE NEXT
          DECISION. No secondary button and no "decide later" link — the sequence
          runs straight through to the name step.
        */}
        {step === "size" ? (
          <PrimaryButton onClick={onContinue} disabled={!selected} data-testid="s5-continue" className="basis-full">
            {t("s5.actions.addName")}
          </PrimaryButton>
        ) : (
          <PrimaryButton
            onClick={() => setStep(STEPS[stepIndex + 1])}
            disabled={!selected}
            data-testid="s5-next"
            className="basis-full"
          >
            {step === "pick"
              ? t("s5.actions.nextColour")
              : step === "colour"
                ? t("s5.actions.nextLayout")
                : t("s5.actions.nextSize")}
          </PrimaryButton>
        )}
      </StickyBar>

    </FunnelPage>
  );
}
